import React from "react";
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaShieldAlt, FaUserShield, FaDatabase, FaGlobe, FaServer, FaUserLock, FaRegClock, FaChild, FaClipboardList, FaBell } from 'react-icons/fa';
import { analytics } from "../features/firebase/firebase-config";
import SEO from "../features/seo/seo.feature";

const Privacy: React.FC = () => {
  analytics.logEvent('page_view', {
    page_title: 'privacy',
  });

  // Define structured data for better SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy | PrepBestie",
    "description": "Privacy Policy for PrepBestie - Detailed information about how we collect, use, and protect your personal data when using our AI-powered practice platform",
    "publisher": {
      "@type": "Organization",
      "name": "PrepBestie",
      "logo": {
        "@type": "ImageObject",
        "url": "https://prepbestie.com/prepbestie.png"
      }
    },
    "inLanguage": "en",
    "lastReviewed": new Date().toISOString().split('T')[0],
    "mainContentOfPage": {
      "@type": "WebPageElement",
      "cssSelector": ".privacy-content"
    },
    "speakable": {
      "@type": "SpeakableSpecification",
      "cssSelector": [".privacy-introduction", ".privacy-rights"]
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SEO
        title='Privacy Policy | PrepBestie'
        description='Privacy Policy for PrepBestie platform - How we collect, use, share, and protect your personal data including information about cookies, data security, your privacy rights, and international transfers.'
        name='PrepBestie'
        type='website'
        structuredData={structuredData}
        keywords={[
          'Prep Bestie',
          'PrepBestie',
          'Privacy Policy',
          'Data Protection',
          'User Privacy',
          'GDPR',
          'CCPA',
          'Interviews',
          'Interview preparation',
          'Pitch preparation',
          'Public speech',
          'AI',
          'AI tools',
          'Data security',
          'Personal data usage',
          'Cookie policy',
          'Privacy rights',
          'Data processing'
        ]}
      />

      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">Privacy Policy</h1>
            <Link
              to="/"
              className="flex items-center text-indigo-600 hover:text-indigo-800 transition"
            >
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          className="bg-white shadow-md rounded-xl p-8 mb-8 privacy-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-2xl font-semibold text-gray-800 mb-6">Privacy Policy for PrepBestie</h2>
          <p className="text-gray-600 mb-4"><strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

          <p className="text-gray-600 mb-8 privacy-introduction">
            At PrepBestie ("we", "us", "our"), we take your privacy seriously. This Privacy Policy outlines how we collect, use, disclose, and safeguard your information when you use our website and services (collectively, the "Services"). Please read this policy carefully to understand our practices regarding your personal data and how we will treat it.
          </p>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaUserShield className="text-indigo-600 mr-2" />
              1. Information We Collect
            </h3>
            <h4 className="text-lg font-medium text-gray-800 mb-2">1.1 Personal Information</h4>
            <p className="text-gray-600 mb-4">
              We may collect the following personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Account Information:</strong> When you create an account, we collect your name, email address, and password.</li>
              <li><strong>Profile Information:</strong> You may choose to provide additional information such as your job title, industry, professional background, and profile picture.</li>
              <li><strong>Payment Information:</strong> If you subscribe to our premium services, we collect payment information, which is processed by our secure payment processors.</li>
              <li><strong>Communication Data:</strong> Information you provide when contacting us, including support requests and feedback.</li>
              <li><strong>Content Data:</strong> Information you provide during practice sessions, including text responses, audio recordings, and video recordings if you use our video features.</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">1.2 Usage Data</h4>
            <p className="text-gray-600 mb-4">
              We automatically collect certain information when you use our Services:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Device Information:</strong> We collect information about your device, including IP address, browser type and version, operating system, and device identifiers.</li>
              <li><strong>Log Data:</strong> We record information about your use of our Services, such as pages visited, time spent on pages, features used, and interactions with content.</li>
              <li><strong>Performance Data:</strong> Information about your practice session performance, including response time, speech patterns, and feedback received.</li>
              <li><strong>Analytics Data:</strong> We use analytics tools to collect information about how users interact with our Services.</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">1.3 Cookies and Similar Technologies</h4>
            <p className="text-gray-600 mb-4">
              We use cookies and similar tracking technologies to track activity on our Services and hold certain information. For more detailed information about our use of cookies, please see our <Link to="/cookies" className="text-indigo-600 hover:text-indigo-800 transition">Cookie Policy</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaDatabase className="text-indigo-600 mr-2" />
              2. How We Use Your Information
            </h3>
            <p className="text-gray-600 mb-4">
              We use your information for various purposes, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Providing and Improving Services:</strong> To operate, maintain, and enhance our Services, including personalization and development of new features.</li>
              <li><strong>AI-Powered Feedback:</strong> To provide AI-generated feedback, analysis, and recommendations based on your practice sessions.</li>
              <li><strong>Authentication and Security:</strong> To verify your identity, secure your account, and protect against fraudulent or unauthorized activity.</li>
              <li><strong>Communication:</strong> To respond to your inquiries, provide support, and send administrative messages, updates, and marketing communications (which you can opt out of).</li>
              <li><strong>Analytics and Research:</strong> To understand user behavior, monitor trends, and improve our Services.</li>
              <li><strong>Personalization:</strong> To tailor content, recommendations, and features to your preferences and needs.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
              <li><strong>AI Model Training:</strong> With your explicit consent, we may use anonymized data to improve our AI models and algorithms.</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">2.1 Legal Bases for Processing</h4>
            <p className="text-gray-600 mb-4">
              We process your personal information under the following legal bases:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Contract Performance:</strong> Processing necessary to fulfill our contractual obligations to you, including providing our Services.</li>
              <li><strong>Legitimate Interests:</strong> Processing necessary for our legitimate interests, such as improving our Services, preventing fraud, and ensuring security.</li>
              <li><strong>Consent:</strong> Processing based on your consent, such as for marketing communications or AI model training.</li>
              <li><strong>Legal Obligation:</strong> Processing necessary to comply with legal requirements.</li>
            </ul>
            <p className="text-gray-600 mb-4">
              For more detailed information about how we process your data, please refer to our <Link to="/data-processing" className="text-indigo-600 hover:text-indigo-800 transition">Data Processing Policy</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaGlobe className="text-indigo-600 mr-2" />
              3. Sharing of Information
            </h3>
            <p className="text-gray-600 mb-4">
              We may share your information in the following circumstances:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Service Providers:</strong> We share information with third-party vendors, consultants, and service providers who perform services on our behalf, such as hosting providers, payment processors, analytics providers, and customer support services.</li>
              <li><strong>Business Transfers:</strong> If we are involved in a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred as part of that transaction.</li>
              <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities (e.g., a court or government agency).</li>
              <li><strong>Protection of Rights:</strong> We may disclose your information to protect the rights, property, or safety of PrepBestie, our users, or others.</li>
              <li><strong>With Your Consent:</strong> We may share your information with third parties when you have given your consent to do so.</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">3.1 International Data Transfers</h4>
            <p className="text-gray-600 mb-4">
              Your information may be transferred to, and processed in, countries other than the country in which you reside. These countries may have data protection laws that are different from those in your country. We take appropriate safeguards to require that your personal information will remain protected in accordance with this Privacy Policy.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaServer className="text-indigo-600 mr-2" />
              4. Third-Party Services
            </h3>
            <p className="text-gray-600 mb-4">
              Our Services may contain links to third-party websites, services, or content that are not owned or controlled by PrepBestie. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services.
            </p>
            <p className="text-gray-600 mb-4">
              We may use third-party services, such as:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Google Analytics:</strong> For analytics and site usage tracking</li>
              <li><strong>Stripe/PayPal:</strong> For payment processing</li>
              <li><strong>AWS/Google Cloud:</strong> For hosting and infrastructure</li>
              <li><strong>Intercom/Zendesk:</strong> For customer support</li>
            </ul>
            <p className="text-gray-600 mb-4">
              These third-party services have their own privacy policies, which we encourage you to review. We are not responsible for the privacy practices or content of these third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaShieldAlt className="text-indigo-600 mr-2" />
              5. Data Security
            </h3>
            <p className="text-gray-600 mb-4">
              We implement appropriate technical and organizational measures to protect your personal information against unauthorized or unlawful processing, accidental loss, destruction, or damage. These measures include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Encryption:</strong> We use encryption technologies to protect sensitive data in transit and at rest.</li>
              <li><strong>Access Controls:</strong> We limit access to your personal information to employees, contractors, and agents who need to know that information to provide services to you.</li>
              <li><strong>Regular Security Assessments:</strong> We conduct regular security assessments and audits of our systems and processes.</li>
              <li><strong>Secure Infrastructure:</strong> We use industry-standard security practices for our hosting and infrastructure.</li>
            </ul>
            <p className="text-gray-600 mb-4">
              While we strive to use commercially acceptable means to protect your personal information, no method of transmission over the Internet or method of electronic storage is 100% secure. We cannot guarantee its absolute security.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaRegClock className="text-indigo-600 mr-2" />
              6. Data Retention
            </h3>
            <p className="text-gray-600 mb-4">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. The criteria used to determine our retention periods include:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>The duration of your relationship with us (e.g., how long you have an account)</li>
              <li>Our legal obligations (e.g., regulatory requirements to keep certain information)</li>
              <li>Limitation periods for legal claims</li>
              <li>Guidelines from relevant data protection authorities</li>
            </ul>
            <p className="text-gray-600 mb-4">
              When your data is no longer necessary for the purposes for which it was collected, we will either delete it or anonymize it. If deletion is not possible (e.g., because your information has been stored in backup archives), we will securely store your information and isolate it from any further processing until deletion is possible.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaChild className="text-indigo-600 mr-2" />
              7. Children's Privacy
            </h3>
            <p className="text-gray-600 mb-4">
              Our Services are not intended for children under the age of 16, and we do not knowingly collect personal information from children under 16. If we learn that we have collected personal information from a child under 16, we will take steps to delete that information as quickly as possible.
            </p>
            <p className="text-gray-600 mb-4">
              If you are a parent or guardian and you believe your child has provided us with personal information, please contact us so that we can take appropriate action.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center privacy-rights">
              <FaUserLock className="text-indigo-600 mr-2" />
              8. Your Rights
            </h3>
            <p className="text-gray-600 mb-4 privacy-rights">
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li><strong>Access:</strong> The right to access the personal information we hold about you.</li>
              <li><strong>Rectification:</strong> The right to request correction of inaccurate personal information.</li>
              <li><strong>Erasure:</strong> The right to request deletion of your personal information in certain circumstances.</li>
              <li><strong>Restriction:</strong> The right to request that we restrict processing of your personal information in certain circumstances.</li>
              <li><strong>Data Portability:</strong> The right to receive your personal information in a structured, commonly used, and machine-readable format.</li>
              <li><strong>Objection:</strong> The right to object to processing of your personal information in certain circumstances.</li>
              <li><strong>Withdraw Consent:</strong> The right to withdraw your consent at any time where we rely on consent to process your personal information.</li>
              <li><strong>Complaint:</strong> The right to lodge a complaint with a supervisory authority.</li>
            </ul>
            <p className="text-gray-600 mb-4">
              To exercise these rights, please contact us using the information provided in the "Contact Us" section below. We will respond to your request within the timeframe required by applicable law.
            </p>

            <h4 className="text-lg font-medium text-gray-800 mb-2">8.1 California Privacy Rights</h4>
            <p className="text-gray-600 mb-4">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA) and the California Privacy Rights Act (CPRA), including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-4 space-y-2">
              <li>The right to know what personal information we collect, use, disclose, and sell</li>
              <li>The right to request deletion of your personal information</li>
              <li>The right to correct inaccurate personal information</li>
              <li>The right to opt-out of the sale or sharing of your personal information</li>
              <li>The right to limit the use and disclosure of sensitive personal information</li>
              <li>The right to non-discrimination for exercising your privacy rights</li>
            </ul>

            <h4 className="text-lg font-medium text-gray-800 mb-2">8.2 European Privacy Rights</h4>
            <p className="text-gray-600 mb-4">
              If you are in the European Economic Area (EEA), United Kingdom, or Switzerland, you have rights under the General Data Protection Regulation (GDPR) and similar laws, as outlined above. In addition to those rights, you may also have the right not to be subject to a decision based solely on automated processing, including profiling, which produces legal effects or similarly significantly affects you.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaBell className="text-indigo-600 mr-2" />
              9. Changes to This Privacy Policy
            </h3>
            <p className="text-gray-600 mb-4">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this page. For significant changes, we will provide notification through our Services or via email.
            </p>
            <p className="text-gray-600 mb-4">
              We encourage you to review this Privacy Policy periodically for any changes. Your continued use of our Services after any changes to this Privacy Policy constitutes your acceptance of the changes.
            </p>
          </section>

          <section className="mb-8">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <FaClipboardList className="text-indigo-600 mr-2" />
              10. Contact Us
            </h3>
            <p className="text-gray-600 mb-4">
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p className="text-gray-600 mb-4">
              <a href="mailto:privacy@prepbestie.com" className="text-indigo-600 hover:text-indigo-800 transition">privacy@prepbestie.com</a>
            </p>
            <p className="text-gray-600 mb-4">
              We will respond to your inquiry as soon as possible and within the timeframe required by applicable law.
            </p>
          </section>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-gray-600 italic">
              By using PrepBestie's Services, you acknowledge that you have read and understood this Privacy Policy.
            </p>
          </div>
        </motion.div>
      </main>

      <footer className="bg-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-500 text-sm">
          &copy; {new Date().getFullYear()} PrepBestie. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
